<template>
  <main class="landing">
    <header class="site-header">
      <router-link to="/" class="site-brand" aria-label="ComplyOps - página inicial">
        <BrandLogo />
      </router-link>
      <nav class="desktop-nav">
        <router-link :to="{ name: 'landing', hash: '#produto' }" :class="navLinkClass('#produto')">Produto</router-link>
        <router-link :to="{ name: 'landing', hash: '#seguranca' }" :class="navLinkClass('#seguranca')">Segurança</router-link>
        <router-link :to="{ name: 'landing', hash: '#planos' }" :class="navLinkClass('#planos')">Planos</router-link>
        <router-link :to="{ name: 'landing', hash: '#faq' }" :class="navLinkClass('#faq')">FAQ</router-link>
        <router-link :to="{ name: 'landing', hash: '#contato' }" :class="navLinkClass('#contato')">Contato</router-link>
      </nav>
      <router-link to="/login" class="header-access">Acessar</router-link>
      <nav class="mobile-nav">
        <router-link :to="{ name: 'landing', hash: '#produto' }" :class="navLinkClass('#produto')">Produto</router-link>
        <router-link :to="{ name: 'landing', hash: '#seguranca' }" :class="navLinkClass('#seguranca')">Segurança</router-link>
        <router-link :to="{ name: 'landing', hash: '#planos' }" :class="navLinkClass('#planos')">Planos</router-link>
        <router-link :to="{ name: 'landing', hash: '#faq' }" :class="navLinkClass('#faq')">FAQ</router-link>
        <router-link :to="{ name: 'landing', hash: '#contato' }" :class="navLinkClass('#contato')">Contato</router-link>
        <router-link to="/login" class="header-access header-access--mobile">Acessar</router-link>
      </nav>
    </header>

    <section class="hero-section reveal-section">
      <div class="hero-copy">
        <p class="hero-kicker">Gestão documental para operações jurídicas</p>
        <span class="hero-plus" aria-hidden="true">+</span>
        <h1>
          Crie, valide e gerencie<br>
          documentos<br>
          jurídicos
        </h1>
        <p>Crie peças, contratos, notificações e demais documentos jurídicos, revise riscos e organize evidências e decisões em um fluxo seguro.</p>
        <div class="hero-actions">
          <router-link class="primary-action" to="/criar-conta">Começar trial</router-link>
          <router-link class="secondary-action" :to="{ name: 'landing', hash: '#planos' }">Ver planos</router-link>
        </div>
      </div>
    </section>

    <section id="produto" class="pain-section reveal-section">
      <p class="section-label">Os desafios que estamos aqui para resolver</p>
      <h2>Sua empresa também enfrenta isso?</h2>
      <div class="pain-grid">
        <article v-for="pain in pains" :key="pain.title">
          <v-icon>{{ pain.icon }}</v-icon>
          <h3>{{ pain.title }}</h3>
          <p>{{ pain.text }}</p>
        </article>
      </div>
    </section>

    <section id="seguranca" class="security-section reveal-section">
      <div class="section-heading">
        <p class="section-label">Segurança</p>
        <h2>Documentos gerados, validados e gerenciados com rastreabilidade.</h2>
      </div>
      <div class="security-grid">
        <article v-for="item in securityItems" :key="item.title">
          <v-icon>{{ item.icon }}</v-icon>
          <div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.text }}</p>
          </div>
        </article>
      </div>
    </section>

    <section id="planos" class="pricing-section reveal-section">
      <div class="section-heading">
        <p class="section-label">Planos</p>
        <h2>Escolha o volume ideal para sua operação jurídica.</h2>
        <p>Todos os planos começam com 7 dias de trial, incluindo 3 créditos, e podem ser ativados com cobrança mensal na área de conta.</p>
      </div>
      <div class="pricing-tabs" role="tablist" aria-label="Perfil dos planos">
        <button
          v-for="group in planGroups"
          :key="group.value"
          type="button"
          role="tab"
          :aria-selected="activePlanGroup === group.value ? 'true' : 'false'"
          :class="{ 'is-active': activePlanGroup === group.value }"
          @click="activePlanGroup = group.value"
        >
          {{ group.label }}
        </button>
      </div>
      <div class="pricing-grid">
        <article v-for="plan in visiblePlans" :key="plan.slug" :class="{ 'is-featured': plan.featured }">
          <div class="pricing-card-header">
            <div class="pricing-title-row">
              <h3>{{ plan.name }}</h3>
              <span v-if="plan.featured">Mais escolhido</span>
            </div>
            <p>{{ plan.description }}</p>
          </div>
          <div class="pricing-price">
            <strong>{{ money(plan.monthly_price) }}</strong>
            <span>/mês</span>
          </div>
          <ul>
            <li v-for="feature in plan.features" :key="feature">
              <v-icon>mdi-check-circle-outline</v-icon>
              <span>{{ feature }}</span>
            </li>
          </ul>
          <router-link class="pricing-action" to="/criar-conta">Começar trial</router-link>
        </article>
      </div>
    </section>

    <section id="faq" class="faq-section reveal-section">
      <div class="section-heading">
        <p class="section-label">Perguntas frequentes</p>
        <h2>Perguntas frequentes sobre a ComplyOps.</h2>
      </div>
      <div class="faq-list">
        <article v-for="item in faqs" :key="item.question">
          <h3>{{ item.question }}</h3>
          <p>{{ item.answer }}</p>
        </article>
      </div>
    </section>

    <section id="contato" class="contact-section reveal-section">
      <div>
        <p class="section-label">Contato</p>
        <h2>Ficou com alguma dúvida?</h2>
        <p>Envie sua pergunta sobre implantação, segurança, cobrança ou fluxos de geração, validação e gestão documental. Retornaremos com uma orientação objetiva.</p>
      </div>

      <form class="contact-form" @submit.prevent="submitContact">
        <div class="form-grid">
          <input v-model="contact.name" type="text" placeholder="Nome" required>
          <input v-model="contact.email" type="email" placeholder="Email corporativo" required>
        </div>
        <input v-model="contact.company" type="text" placeholder="Empresa ou escritório">
        <textarea v-model="contact.message" rows="5" placeholder="Como podemos ajudar?" required></textarea>
        <button type="submit">Enviar mensagem</button>
        <p v-if="contactSent" class="contact-feedback">Mensagem registrada. Nossa equipe entrará em contato.</p>
      </form>
    </section>

    <footer class="site-footer">
      <p>© {{ currentYear }} ComplyOps. Todos os direitos reservados.</p>
      <div class="footer-powered">
        <span>Powered by</span>
        <img :src="mazzitechLogo" alt="Mazzitech">
      </div>
    </footer>
  </main>
</template>

<script>
import BrandLogo from '../../components/BrandLogo.vue'
import mazzitechLogo from '../../assets/images/logo-mazzitech.png'

export default {
  name: 'LandingPage',
  components: {
    BrandLogo
  },
  mounted () {
    this.activeSectionHash = this.$route.hash || '#produto'
    window.addEventListener('scroll', this.handleSectionScroll, { passive: true })
    this.$nextTick(() => {
      this.initScrollReveal()
      this.scrollToCurrentHash('auto')
      this.updateActiveSection()
    })
    window.setTimeout(() => {
      this.scrollToCurrentHash('auto')
      this.updateActiveSection()
    }, 300)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleSectionScroll)
    if (this.scrollFrame) {
      window.cancelAnimationFrame(this.scrollFrame)
    }
    if (this.revealObserver) {
      this.revealObserver.disconnect()
    }
  },
  watch: {
    '$route.hash' () {
      this.activeSectionHash = this.$route.hash || '#produto'
      this.$nextTick(() => this.scrollToCurrentHash())
    }
  },
  data () {
    return {
      activeSectionHash: '#produto',
      scrollFrame: null,
      revealObserver: null,
      mazzitechLogo,
      contactSent: false,
      contact: {
        name: '',
        email: '',
        company: '',
        message: ''
      },
      activePlanGroup: 'standard',
      planGroups: [
        { label: 'Individual', value: 'standard' },
        { label: 'Corporativo', value: 'high_volume' }
      ],
      pains: [
        { icon: 'mdi-file-document-edit-outline', title: 'Minutas começam do zero?', text: 'Crie peças, contratos e notificações com contexto e padrão do escritório.' },
        { icon: 'mdi-shield-alert-outline', title: 'Documentos saem sem revisão?', text: 'Valide riscos, dados sensíveis e fundamento jurídico antes do uso.' },
        { icon: 'mdi-folder-search-outline', title: 'Arquivos espalhados?', text: 'Centralize documentos, evidências e decisões em uma trilha pesquisável.' },
        { icon: 'mdi-account-check-outline', title: 'Quem aprovou o documento?', text: 'Registre responsável, motivo, ressalvas e versão final de cada decisão.' }
      ],
      securityItems: [
        { icon: 'mdi-file-document-edit-outline', title: 'Geração assistida', text: 'Minutas podem nascer com contexto e seguir para validação no mesmo fluxo.' },
        { icon: 'mdi-shield-check-outline', title: 'Validação com políticas', text: 'Regras internas indicam quando um documento pode ser liberado, ajustado ou bloqueado.' },
        { icon: 'mdi-folder-text-outline', title: 'Gestão documental', text: 'Documentos, versões, evidências e decisões ficam organizados para consulta.' },
        { icon: 'mdi-clipboard-text-clock-outline', title: 'Trilha de auditoria', text: 'Ações relevantes ficam registradas para prestação de contas e governança.' }
      ],
      plans: [
        {
          name: 'Starter',
          slug: 'starter',
          group: 'standard',
          monthly_price: 59.9,
          description: 'Para profissionais iniciando geração, validação e gestão documental com trilha auditável.',
          features: [
            '1 usuário',
            '100 créditos por mês'
          ]
        },
        {
          name: 'Professional',
          slug: 'professional',
          group: 'standard',
          monthly_price: 159.9,
          description: 'Para profissionais com mais volume e necessidade de governança recorrente.',
          featured: true,
          features: [
            '1 usuário',
            '500 créditos por mês'
          ]
        },
        {
          name: 'Business',
          slug: 'business',
          group: 'standard',
          monthly_price: 349.9,
          description: 'Para profissionais com alto volume de documentos e maior demanda mensal.',
          features: [
            '1 usuário',
            '2.500 créditos por mês'
          ]
        },
        {
          name: 'Starter Corporativo',
          slug: 'starter_plus',
          group: 'high_volume',
          monthly_price: 149.9,
          description: 'Para escritórios pequenos com uso recorrente em mais documentos por mês.',
          features: [
            'Até 3 usuários',
            '300 créditos por mês'
          ]
        },
        {
          name: 'Professional Corporativo',
          slug: 'professional_plus',
          group: 'high_volume',
          monthly_price: 349.9,
          description: 'Para equipes que processam mais documentos e precisam de maior cota operacional.',
          featured: true,
          features: [
            'Até 10 usuários',
            '1.000 créditos por mês'
          ]
        },
        {
          name: 'Business Corporativo',
          slug: 'business_plus',
          group: 'high_volume',
          monthly_price: 699.9,
          description: 'Para operações com alto volume de geração, validação e auditoria documental.',
          features: [
            'Até 25 usuários',
            '5.000 créditos por mês'
          ]
        }
      ],
      faqs: [
        { question: 'A plataforma substitui o jurídico?', answer: 'Não. A ComplyOps apoia geração, validação, organização e evidências para que a decisão continue com responsáveis internos.' },
        { question: 'As evidências são criadas manualmente?', answer: 'As principais evidências são geradas automaticamente em criações, validações, aprovações e auditorias. O registro manual fica apenas para documentos externos.' },
        { question: 'Como funciona o trial?', answer: 'Novas contas podem usar a plataforma por 7 dias, com limite de 3 créditos antes da assinatura de um plano.' },
        { question: 'É possível controlar limites por usuário?', answer: 'Sim. O plano define a cota total da empresa e cada usuário pode ter um limite individual para evitar consumo concentrado.' }
      ]
    }
  },
  computed: {
    currentYear () {
      return new Date().getFullYear()
    },
    visiblePlans () {
      return this.plans.filter(plan => plan.group === this.activePlanGroup)
    }
  },
  methods: {
    navLinkClass (hash) {
      return {
        'is-active': this.activeSectionHash === hash
      }
    },
    money (value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    initScrollReveal () {
      const sections = Array.from(document.querySelectorAll('.reveal-section'))
      if (!sections.length) return

      const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (prefersReducedMotion || !('IntersectionObserver' in window)) {
        sections.forEach((section) => section.classList.add('is-visible'))
        return
      }

      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return

          entry.target.classList.add('is-visible')
          this.revealObserver.unobserve(entry.target)
        })
      }, {
        rootMargin: '0px 0px -14% 0px',
        threshold: 0.18
      })

      sections.forEach((section) => this.revealObserver.observe(section))
    },
    scrollToCurrentHash (behavior = 'smooth') {
      const hash = this.$route.hash || window.location.hash
      if (!hash) return

      const target = document.querySelector(hash)
      if (!target) return

      const header = document.querySelector('.site-header')
      const offset = header ? header.offsetHeight : 70
      const top = target.getBoundingClientRect().top + window.pageYOffset - offset
      window.scrollTo({ top, behavior })
    },
    handleSectionScroll () {
      if (this.scrollFrame) return

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null
        this.updateActiveSection()
      })
    },
    updateActiveSection () {
      const sectionHashes = ['#produto', '#seguranca', '#planos', '#faq', '#contato']
      const header = document.querySelector('.site-header')
      const headerOffset = header ? header.offsetHeight : 70
      const activationLine = headerOffset + 220
      let activeHash = '#produto'

      sectionHashes.forEach((hash) => {
        const section = document.querySelector(hash)
        if (!section) return

        const rect = section.getBoundingClientRect()
        if (rect.top <= activationLine) {
          activeHash = hash
        }
      })

      const pageBottom = window.pageYOffset + window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      if (pageBottom >= documentHeight - 8) {
        activeHash = '#contato'
      }

      this.activeSectionHash = activeHash
    },
    submitContact () {
      this.contactSent = true
      this.contact = {
        name: '',
        email: '',
        company: '',
        message: ''
      }
    }
  }
}
</script>
